/**
 * The billing lock, as far as the router and the API client need to know it.
 *
 * A dependency-free leaf on purpose: `api/client.ts` imports it to say a
 * request was refused with `402 BILLING_LOCKED`, so it must not import
 * anything that imports the client back. The lock itself - its reason, dates
 * and amount - is read from `/auth/me` by `AuthProvider`; this module only
 * carries the word that it is time to read it again.
 */

/** Where a locked merchant is sent: the subscribe screen, wearing the lock banner. */
export const LOCKED_SUBSCRIBE_PATH = '/subscribe?locked=1';

/**
 * Dashboard paths a locked merchant may still open.
 *
 * Billing, for history, receipts and credit notes; the subscribe screen, to
 * pay; the password screen, because the API refuses everything else while a
 * forced change holds; and the policies, which nobody should need a paid plan
 * to read. Mirrors the server's own allowlist (`BillingTenant`).
 */
const OPEN_WHILE_LOCKED = ['/subscribe', '/app/billing', '/change-password', '/legal'];

export function openWhileLocked(pathname: string): boolean {
  return OPEN_WHILE_LOCKED.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

const listeners = new Set<() => void>();

/**
 * The API client saw a `402 BILLING_LOCKED`. Whoever is listening - the auth
 * provider - re-reads `/auth/me`, and the route guard takes it from there.
 */
export function reportBillingLock(): void {
  for (const listener of [...listeners]) listener();
}

export function onBillingLocked(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
